import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState([]);

    // Загрузка корзины из localStorage при инициализации
    useEffect(() => {
        const savedCart = localStorage.getItem('budkom_cart');
        if (savedCart) {
            try {
                setCartItems(JSON.parse(savedCart));
            } catch (error) {
                console.error('Error parsing cart from localStorage:', error);
                setCartItems([]);
            }
        }
    }, []);

    // Сохранение корзины в localStorage при изменении
    useEffect(() => {
        localStorage.setItem('budkom_cart', JSON.stringify(cartItems));
    }, [cartItems]);

    // Добавить товар в корзину
    const addToCart = (product, selectedSize, quantity = 1) => {
        setCartItems(prevItems => {
            const existingItemIndex = prevItems.findIndex(
                item => item.id === product.id && item.selectedSize === selectedSize
            );

            if (existingItemIndex > -1) {
                // Если товар с таким размером уже есть, увеличиваем количество
                const updatedItems = [...prevItems];
                updatedItems[existingItemIndex].quantity += quantity;
                return updatedItems;
            } else {
                // Добавляем новый товар
                return [...prevItems, {
                    id: product.id,
                    name: product.name,
                    price: product.price,
                    image: product.images ? JSON.parse(product.images)[0] : null,
                    selectedSize,
                    quantity
                }];
            }
        });
    };

    // Удалить товар из корзины
    const removeFromCart = (productId, selectedSize) => {
        setCartItems(prevItems =>
            prevItems.filter(item => !(item.id === productId && item.selectedSize === selectedSize))
        );
    };

    // Обновить количество товара
    const updateQuantity = (productId, selectedSize, newQuantity) => {
        if (newQuantity <= 0) {
            removeFromCart(productId, selectedSize);
            return;
        }

        setCartItems(prevItems =>
            prevItems.map(item =>
                item.id === productId && item.selectedSize === selectedSize
                    ? { ...item, quantity: newQuantity }
                    : item
            )
        );
    };

    // Очистить корзину
    const clearCart = () => {
        setCartItems([]);
    };

    // Получить общую стоимость
    const getTotalPrice = () => {
        return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    };

    // Получить общее количество товаров
    const getTotalItems = () => {
        return cartItems.reduce((total, item) => total + item.quantity, 0);
    };

    const value = {
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotalPrice,
        getTotalItems
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};